import { useState, useEffect, memo, MouseEvent } from 'react'
import styles from '../PostsCalendarView.pcss'
import { CalendarDay } from 'interfaces/Post/Calendar'
import { PlannedPost } from 'interfaces'
import { DragOverlay } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable'
import { createPortal } from 'react-dom'
import { CalendarPostSize } from 'services/post/state'
import MiniPostView, { MiniPostViewProps } from '../../MiniPostView'
import { FormattedMessage } from 'react-intl'

const DEFAULT_VISIBLE_POSTS = 10

export type IPostGroup = {
  order: number
  date: string // MM-DD-YYYY
  posts: PlannedPost[]
}

type PostsCalendarDayProps = {
  day: CalendarDay
  postGroup: IPostGroup
  activePostId?: string
  draggedPost: PlannedPost | null
  withDragAndDrop: boolean
  postSize: CalendarPostSize
  dimPosted?: boolean
  onPostClick: (post: PlannedPost) => void
  onExpandDay: (day: CalendarDay, container: HTMLDivElement) => void
}

export function PostsCalendarDay({
  day,
  postGroup,
  withDragAndDrop,
  activePostId,
  draggedPost,
  postSize,
  dimPosted,
  onExpandDay,
  onPostClick
}: PostsCalendarDayProps) {
  const { posts, date } = postGroup
  const [order, setOrder] = useState(posts.map(p => p.id))

  const onExpand = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const container = (e.target as HTMLDivElement).closest('[data-list-container]') as HTMLDivElement
    if (container) {
      onExpandDay(day, container)
    }
    return true
  }

  useEffect(() => {
    setOrder(posts.map(p => p.id))
  }, [posts])

  const items = posts.slice(0, DEFAULT_VISIBLE_POSTS).map(post => {
    const isActive = activePostId === post.id

    if (post.isEmpty || post.isPosted || post.isVirtual) {
      return (
        <div className={styles['post-container']} key={post.id}>
          <MiniPostView
            key={post.id}
            post={post}
            size={postSize}
            dimPosted={dimPosted}
            className={`${styles['mini-post']} ${isActive ? styles.active : ''}`}
          />
        </div>
      )
    }

    let dndClass: string = ''
    if (
      !post.isVirtual
      && draggedPost
      && draggedPost.profileId === post.profileId
      && draggedPost.id !== post.id
      && draggedPost.bucketId === post.bucketId
    ) {
      dndClass = styles['drop-target']
    }

    return (
      <DraggableCalendarPost
        key={post.id}
        post={post}
        size={postSize}
        containerClassName={dndClass}
        className={`${styles['mini-post']} ${isActive ? styles.active : ''}`}
        onClick={onPostClick}
      />
    )
  })

  const content = withDragAndDrop ? (
    <SortableContext id={date} items={order} strategy={verticalListSortingStrategy}>
      <div style={{ height: '100%' }}>
        {items}
      </div>
    </SortableContext>
  ) : (
    <div className={styles['scrollable-box']}>
      {items}
    </div>
  )

  const defaultVisiblePostsCount = Math.min(DEFAULT_VISIBLE_POSTS, posts.length)
  let hiddenNonEmptyPostsCount = 0
  let hiddenEmptyPostsCount = 0
  for (let i = defaultVisiblePostsCount; i < posts.length; i++) {
    if (posts[i].isEmpty) hiddenEmptyPostsCount++
    else hiddenNonEmptyPostsCount++
  }

  const expandLabel = hiddenNonEmptyPostsCount > 0
    ? `+ ${hiddenNonEmptyPostsCount} more`
    : hiddenEmptyPostsCount > 0
      ? <FormattedMessage id="post.calendar.btn-slots" values={{ count: hiddenEmptyPostsCount }} />
      : undefined

  const showExpandButton = posts.length > DEFAULT_VISIBLE_POSTS

  return (
    <div data-list-container>
      {content}
      {expandLabel && showExpandButton && (
        <div className={styles['btn-box']} role="button" onClick={onExpand}>
          <div className={styles.button}>
            {expandLabel}
          </div>
        </div>
      )}
    </div>
  )
}

export const DraggableCalendarPost = memo((props: MiniPostViewProps & { containerClassName: string }) => {
  const { attributes, listeners, setDroppableNodeRef, setDraggableNodeRef, isDragging } = useSortable({
    id: props.post.id,
    data: { post: props.post },
    disabled: !props.post.autoPost || props.post.isPosted
  })

  const disabled = props.post.isEmpty || props.post.isPosted || (props.post as any).isVirtual
  const draggableProps = disabled
    ? undefined
    : {
      attributes,
      listeners,
      isDragging,
      style: { opacity: isDragging ? '0.5' : '1' }
    }

  return (
    <div className={`${styles['post-container']} ${props.containerClassName}`} ref={disabled ? undefined : setDroppableNodeRef}>
      <MiniPostView
        {...props}
        ref={setDraggableNodeRef}
        dimPosted={props.dimPosted}
        draggableProps={draggableProps}
      />
      {isDragging && createPortal(
        <DragOverlay>
          <MiniPostView {...props} />
        </DragOverlay>,
        document.body
      )}
    </div>

  )
}, (prevProps, nextProps) => {
  return prevProps.containerClassName === nextProps.containerClassName
    && prevProps.size === nextProps.size
    && prevProps.className === nextProps.className
    && prevProps.post.time === nextProps.post.time
})
